import React, { type ReactNode, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Scrollbars } from 'react-custom-scrollbars';
import { CollapseToggle, CollapseWrapper } from '@components/Collapse';
import IconHelp from '@components/icons/IconHelp';
import IconReset from '@components/icons/IconReset';
import IconClose from '@components/icons/IconClose';
import Button from '@components/Button';
import { showConfirmModal } from '@components/ConfirmModal';
import {
  ValidatorWrapper,
  type ValidatorWrapperHandlers,
} from '@components/Validator';
import { $panelApi } from '@stores/panelStores';
import { TipWrapper } from '@components/TipWrapper';

interface Props {
  title: ReactNode;
  children: ReactNode;
  isChanged?: boolean | (() => boolean);
  onRequestClose?: () => void;
  onHelpClick?: () => void;
  onResetClick?: () => void;
  onDeleteClick?: () => void;
  onConfirm: () => void;
  onAfterClose?: () => void;
}

const slideIn = keyframes`
  from {
    transform: translate3d(100%, 0, 0);
    visibility: visible;
  }
  to {
    transform: translate3d(0, 0, 0);
  }`;

const Panel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 270px;
  box-shadow: -5px 0 10px 0 rgba(0, 0, 0, 0.5);
  animation-name: ${slideIn};
  animation-duration: 300ms;
`;

function PanelBase({
  title,
  children,
  isChanged = false,
  onRequestClose = $panelApi.dismiss,
  onHelpClick,
  onResetClick,
  onDeleteClick,
  onConfirm,
  onAfterClose,
}: Props) {
  const validRef = useRef<ValidatorWrapperHandlers>(null);

  const closePanel = () => {
    onRequestClose();
    if (onAfterClose) {
      onAfterClose();
    }
  };

  const handleClose = async () => {
    const changed = typeof isChanged === 'function' ? isChanged() : isChanged;
    if (changed) {
      const { confirmed } = await showConfirmModal({
        title: '有未保存的更改',
        content: '当前所有未经保存的改动将丢失, 是否继续?',
      });
      if (!confirmed) return;
    }
    closePanel();
  };

  const handleConfirm = () => {
    if (validRef.current?.validityCheck()) {
      onConfirm();
      onRequestClose();
    }
  };

  const handleDeleteClick = () => {
    if (onDeleteClick) {
      onDeleteClick();
    }
    onRequestClose();
  };

  return (
    <ValidatorWrapper ref={validRef}>
      <CollapseWrapper>
        <Panel className="z-40 flex flex-col bg-black text-base text-white">
          <div className="flex h-50 items-center px-10">
            {title}
            {!!onHelpClick && (
              <TipWrapper
                className="ml-10"
                text="快速了解该功能"
                placement="bottom"
                size="sm"
              >
                <IconHelp onClick={onHelpClick} />
              </TipWrapper>
            )}
            {!!onResetClick && (
              <TipWrapper
                className="ml-10"
                text="恢复默认设置"
                placement="bottom"
                size="sm"
              >
                <IconReset onClick={onResetClick} />
              </TipWrapper>
            )}
            <CollapseToggle className="ml-10" />
            <IconClose className="ml-auto" onClick={handleClose} />
          </div>
          <div className="flex-grow">
            <Scrollbars>
              <div className="p-10">{children}</div>
            </Scrollbars>
          </div>
          <div className="p-10">
            <Button className="block w-full" onClick={handleConfirm}>
              完成设置
            </Button>
            <Button
              scheme="dark"
              className="mt-5 block w-full"
              onClick={handleClose}
            >
              取消设置
            </Button>
            {!!onDeleteClick && (
              <Button
                scheme="darkest"
                className="mt-5 block w-full"
                onClick={handleDeleteClick}
              >
                删除
              </Button>
            )}
          </div>
        </Panel>
      </CollapseWrapper>
    </ValidatorWrapper>
  );
}

export default PanelBase;
